import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Share2,
  Loader2,
  FileText,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { socialPostingService } from '../services/socialPosting';
import { PostDetail } from '../components/social-posting/PostDetail';
import type { SocialPost } from '../services/socialPosting';

// ── Create Workspace Dialog ───────────────────────────────────

function CreateWorkspaceDialog({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      setIsCreating(true);
      await onCreate(name.trim());
      setName('');
      onClose();
    } catch {
      // handled
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Create Workspace</DialogTitle>
          <DialogDescription className="text-sm">
            A workspace holds your products and designs together.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 pt-3">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workspace name..."
            className="h-12 rounded-xl text-base"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          />
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" className="rounded-lg h-10 px-5 text-sm" onClick={onClose}>
              Cancel
            </Button>
            <Button className="rounded-lg h-10 px-5 text-sm" onClick={handleCreate} disabled={!name.trim() || isCreating}>
              {isCreating && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Post List ─────────────────────────────────────────────────

function PostList({
  posts,
  isLoading,
  onSelect,
  onCreate,
}: {
  posts: SocialPost[];
  isLoading: boolean;
  onSelect: (post: SocialPost) => void;
  onCreate: (name: string) => Promise<void>;
}) {
  const [showCreate, setShowCreate] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const statusColor = (status: string) => {
    if (status === 'active') return 'bg-green-500/15 text-green-400 border-green-500/30';
    if (status === 'archived') return 'bg-gray-500/15 text-gray-400 border-gray-500/30';
    return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Social Posting</h1>
          <p className="text-muted-foreground mt-1">
            Create social media posts from your feed products
          </p>
        </div>
        <Button className="rounded-lg" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Workspace
        </Button>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-24">
          <Share2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No workspaces yet.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create a workspace or select products from Feed Management.
          </p>
          <Button className="rounded-lg mt-4" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Workspace
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((post) => (
            <Card
              key={post.id}
              className="border-2 rounded-xl p-5 hover:border-primary/40 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              onClick={() => onSelect(post)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <h3 className="text-base font-semibold truncate">{post.name}</h3>
                </div>
                <Badge variant="outline" className={statusColor(post.status)}>
                  {post.status}
                </Badge>
              </div>
              {post.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{post.description}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Created {new Date(post.created_at).toLocaleDateString()}
              </p>
            </Card>
          ))}
        </div>
      )}

      <CreateWorkspaceDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreate={onCreate}
      />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────

export function SocialPostingPage() {
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadPosts = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await socialPostingService.listPosts(1, 100);
      setPosts(result.data);
      return result.data;
    } catch {
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts().then((loadedPosts) => {
      const postId = searchParams.get('postId');
      if (postId && loadedPosts.length > 0) {
        const found = loadedPosts.find((p) => p.id === postId);
        if (found) setSelectedPost(found);
      }
    });
  }, [loadPosts, searchParams]);

  const handleCreate = async (name: string) => {
    const post = await socialPostingService.createPost({ name });
    setPosts((prev) => [post, ...prev]);
    setSelectedPost(post);
  };

  const handlePostUpdated = (updated: SocialPost) => {
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    setSelectedPost(updated);
  };

  const handlePostDeleted = () => {
    if (selectedPost) {
      setPosts((prev) => prev.filter((p) => p.id !== selectedPost.id));
    }
    setSelectedPost(null);
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        {selectedPost ? (
          <PostDetail
            post={selectedPost}
            onBack={() => setSelectedPost(null)}
            onPostUpdated={handlePostUpdated}
            onPostDeleted={handlePostDeleted}
          />
        ) : (
          <PostList
            posts={posts}
            isLoading={isLoading}
            onSelect={setSelectedPost}
            onCreate={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
